/**
 * AccountsOverviewChart - Account growth trend visualization
 *
 * Features:
 * - Area chart with gradient fill
 * - Multi-line: total accounts, active, trial
 * - Custom tooltip matching app design
 * - Responsive container
 * - 12-month historical data
 */
import { cn, chartA11yProps } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ============================================
// Types
// ============================================

interface AccountTrendDataPoint {
  date: string;
  total: number;
  active: number;
  trial: number;
}

interface AccountsOverviewChartProps {
  data?: AccountTrendDataPoint[];
  className?: string;
}

// ============================================
// Formatters
// ============================================

const numberFormatter = new Intl.NumberFormat('en-US');

function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

function formatMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short' });
}

// ============================================
// Custom Tooltip
// ============================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
        {label
          ? new Date(label).toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })
          : ''}
      </p>
      <div className="space-y-1.5">
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-300 capitalize">
                {entry.name}
              </span>
            </div>
            <span className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function AccountsOverviewChart({ data, className }: AccountsOverviewChartProps) {
  const chartData = data ?? [];

  if (!chartData.length) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Accounts Overview</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-sm text-slate-500">No data available</p>
        </CardContent>
      </Card>
    );
  }

  // Non-null assertions are safe: the `!chartData.length` early return above guarantees both ends exist.
  const first = chartData[0]!;
  const last = chartData[chartData.length - 1]!;
  const chartLabel =
    `Account growth over ${chartData.length} month${chartData.length === 1 ? '' : 's'}, ${formatMonth(first.date)} to ${formatMonth(last.date)}: ` +
    `total accounts went from ${formatNumber(first.total)} to ${formatNumber(last.total)}, ` +
    `active from ${formatNumber(first.active)} to ${formatNumber(last.active)}, ` +
    `trial from ${formatNumber(first.trial)} to ${formatNumber(last.trial)}.`;

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Accounts Growth</CardTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          12-month account growth trend
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              {...chartA11yProps(chartLabel)}
            >
              <defs>
                <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="trialGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e2e8f0"
                className="dark:stroke-slate-700"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tickFormatter={formatMonth}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                dy={8}
              />

              <YAxis
                tickFormatter={(value) => formatNumber(value)}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                width={50}
              />

              <Tooltip content={<CustomTooltip />} />

              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value: string) => (
                  <span className="text-xs text-slate-600 dark:text-slate-300 capitalize">
                    {value}
                  </span>
                )}
              />

              <Area
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#totalGradient)"
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: '#3b82f6',
                  strokeWidth: 2,
                  fill: '#fff',
                }}
              />

              <Area
                type="monotone"
                dataKey="active"
                name="Active"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#activeGradient)"
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: '#10b981',
                  strokeWidth: 2,
                  fill: '#fff',
                }}
              />

              <Area
                type="monotone"
                dataKey="trial"
                name="Trial"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#trialGradient)"
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: '#f59e0b',
                  strokeWidth: 2,
                  fill: '#fff',
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function AccountsOverviewChartSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mt-1" />
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
      </CardContent>
    </Card>
  );
}
